/**
 * Browser-side S3 client for the project's configured asset origin.
 * Uses aws4fetch for SigV4 signing — works against any S3-compatible
 * endpoint (AWS S3, Cloudflare R2, MinIO, Backblaze B2, Wasabi).
 *
 * Three operations cover v1: list, upload, resolve. No delete yet —
 * the gallery is a picker, not a manager. Add when needed.
 */

import type { AssetOriginConfig } from '@bliksys/shared';
import { AwsClient } from 'aws4fetch';

export interface AssetItem {
  /** Full object key, e.g. `assets/hero.jpg`. */
  key: string;
  /** Bytes. -1 if the listing didn't include a Size element. */
  size: number;
  /** ISO-8601 string from the LastModified header, or undefined. */
  lastModified?: string;
  /** Resolved public URL — what we'd write into the document if `assetFormat: 'url'`. */
  url: string;
}

export interface ListResult {
  items: AssetItem[];
  /** When set, pass back as `continuationToken` to fetch the next page. */
  nextToken?: string;
}

/**
 * Map a raw failure into a friendly message. CORS failures show up
 * as `TypeError: Failed to fetch` in the browser with no useful body
 * — we detect that shape and surface a CORS hint specifically, since
 * it's the most common first-time setup snag.
 */
export class AssetOriginError extends Error {
  constructor(
    message: string,
    public readonly kind: 'cors' | 'auth' | 'not-found' | 'network' | 'parse' | 'unknown',
  ) {
    super(message);
    this.name = 'AssetOriginError';
  }
}

export async function listObjects(
  config: AssetOriginConfig,
  options: { prefix?: string; continuationToken?: string; maxKeys?: number } = {},
): Promise<ListResult> {
  const params = new URLSearchParams();
  params.set('list-type', '2');
  const fullPrefix = joinPrefix(config.prefix, options.prefix);
  if (fullPrefix) params.set('prefix', fullPrefix);
  if (options.continuationToken) params.set('continuation-token', options.continuationToken);
  params.set('max-keys', String(options.maxKeys ?? 100));

  const url = `${bucketUrl(config)}?${params.toString()}`;
  const response = await signedFetch(config, url, { method: 'GET' });
  if (!response.ok) {
    throw await mapErrorResponse(response);
  }
  const xml = await response.text();
  return parseListXml(xml, config);
}

export async function uploadObject(
  config: AssetOriginConfig,
  key: string,
  file: File | Blob,
  options: { contentType?: string } = {},
): Promise<AssetItem> {
  const fullKey = joinPrefix(config.prefix, key);
  const url = `${bucketUrl(config)}/${encodeKey(fullKey)}`;
  const contentType =
    options.contentType ?? ((file instanceof File ? file.type : '') || 'application/octet-stream');
  const response = await signedFetch(config, url, {
    method: 'PUT',
    body: file,
    headers: { 'content-type': contentType },
  });
  if (!response.ok) {
    throw await mapErrorResponse(response);
  }
  return {
    key: fullKey,
    size: file.size,
    url: assetUrl(config, fullKey),
  };
}

export async function testConnection(config: AssetOriginConfig): Promise<void> {
  await listObjects(config, { maxKeys: 1 });
}

/**
 * Resolve an object key to its public URL. Uses `publicUrlBase` when
 * set; otherwise reconstructs from the endpoint + bucket using the
 * configured addressing style.
 */
export function assetUrl(config: AssetOriginConfig, key: string): string {
  const base = config.publicUrlBase ? config.publicUrlBase.replace(/\/+$/, '') : bucketUrl(config);
  return `${base}/${encodeKey(key)}`;
}

/* ---------------- internals ---------------- */

function signedFetch(config: AssetOriginConfig, url: string, init: RequestInit): Promise<Response> {
  const aws = new AwsClient({
    accessKeyId: config.accessKeyId,
    secretAccessKey: config.secretAccessKey,
    service: 's3',
    region: config.region,
  });
  return aws.fetch(url, init).catch((err: unknown) => {
    // Fetch rejects with TypeError for network-level failures, which
    // in browsers includes CORS preflight rejections. There is no
    // way to disambiguate from JS — both surface as "Failed to
    // fetch". Treat them as the most likely cause for new origins.
    if (err instanceof TypeError) {
      throw new AssetOriginError(
        'Could not reach the asset origin. This is usually a CORS configuration issue — ' +
          "the bucket needs an explicit CORS rule allowing GET/PUT from this app's origin. " +
          'Verify the endpoint URL is also correct.',
        'cors',
      );
    }
    throw new AssetOriginError(
      err instanceof Error ? err.message : 'Network request failed.',
      'network',
    );
  });
}

async function mapErrorResponse(response: Response): Promise<AssetOriginError> {
  const status = response.status;
  let body = '';
  try {
    body = await response.text();
  } catch {
    // ignore
  }
  if (status === 401 || status === 403) {
    return new AssetOriginError(
      `Auth failed (${status}). Check the access key, secret, and bucket permissions.`,
      'auth',
    );
  }
  if (status === 404) {
    return new AssetOriginError(`Not found (404). Verify the bucket name.`, 'not-found');
  }
  return new AssetOriginError(
    `Asset origin returned ${status}.${body ? ` ${shortenXmlError(body)}` : ''}`,
    'unknown',
  );
}

function shortenXmlError(body: string): string {
  // S3 error envelopes look like <Error><Code>...</Code><Message>...</Message></Error>.
  // Pull out the message if present, otherwise just trim.
  const match = /<Message>([^<]+)<\/Message>/.exec(body);
  if (match?.[1]) return match[1];
  return body.length > 120 ? `${body.slice(0, 117)}...` : body;
}

function parseListXml(xml: string, config: AssetOriginConfig): ListResult {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  if (doc.querySelector('parsererror')) {
    throw new AssetOriginError('Could not parse list response from asset origin.', 'parse');
  }
  const items: AssetItem[] = [];
  for (const node of Array.from(doc.getElementsByTagName('Contents'))) {
    const key = node.getElementsByTagName('Key')[0]?.textContent ?? '';
    if (!key) continue;
    const size = Number(node.getElementsByTagName('Size')[0]?.textContent ?? -1);
    const lastModified = node.getElementsByTagName('LastModified')[0]?.textContent ?? undefined;
    items.push({
      key,
      size: Number.isFinite(size) ? size : -1,
      ...(lastModified ? { lastModified } : {}),
      url: assetUrl(config, key),
    });
  }
  const isTruncated = doc.getElementsByTagName('IsTruncated')[0]?.textContent?.trim() === 'true';
  const nextToken = isTruncated
    ? (doc.getElementsByTagName('NextContinuationToken')[0]?.textContent ?? undefined)
    : undefined;
  return nextToken ? { items, nextToken } : { items };
}

/**
 * Bucket-rooted URL for signing requests, without the trailing key.
 *
 * Path-style:    `<endpoint>/<bucket>`
 * Virtual-host:  `<scheme>://<bucket>.<endpoint-host>`
 *
 * Path-style is required for MinIO, localhost, and any endpoint
 * where the bucket name is not a valid DNS label. AWS S3 supports
 * both; default to virtual-host for AWS to match the modern norm.
 */
function bucketUrl(config: AssetOriginConfig): string {
  const endpoint = config.endpoint.replace(/\/+$/, '');
  if (config.forcePathStyle) {
    return `${endpoint}/${encodeURIComponent(config.bucket)}`;
  }
  const url = new URL(endpoint);
  url.host = `${config.bucket}.${url.host}`;
  // URL serialization adds a trailing slash for bare hosts; strip it
  // so callers can append `/<key>` cleanly.
  return url.toString().replace(/\/+$/, '');
}

function joinPrefix(...parts: (string | undefined)[]): string {
  return parts
    .filter((p): p is string => typeof p === 'string' && p.length > 0)
    .map((p) => p.replace(/^\/+/, ''))
    .join('');
}

function encodeKey(key: string): string {
  // S3 keys can contain `/` to express folder structure — preserve
  // those, encode each segment.
  return key
    .split('/')
    .map((seg) => encodeURIComponent(seg))
    .join('/');
}

/** Exported only for tests. */
export const __test = { parseListXml, bucketUrl, joinPrefix, encodeKey };

/* ---------------- AssetClient abstraction ---------------- */

/**
 * Backend-agnostic interface the AssetGallery talks to. Two implementations:
 *
 *   - `localAssetClient(config)` — free mode. Signs S3 requests directly
 *     in the browser using the inline `AssetOriginConfig`.
 *   - `cloudAssetClient({ baseUrl, orgSlug, originId })` — paid mode
 *     (in `cloud-asset-client.ts`). Talks to the API's asset proxy;
 *     the Worker decrypts the org-scoped credentials server-side. The
 *     browser never holds the access key.
 *
 * Both return matching `ListResult` and `AssetItem` shapes so the
 * gallery is backend-blind.
 */
export interface AssetClient {
  list(options?: { continuationToken?: string; maxKeys?: number }): Promise<ListResult>;
  upload(filename: string, file: File | Blob): Promise<AssetItem>;
}

export function localAssetClient(config: AssetOriginConfig): AssetClient {
  return {
    async list(options = {}) {
      return listObjects(config, options);
    },
    async upload(filename, file) {
      return uploadObject(config, filename, file);
    },
  };
}
